<div class="card">
    <div class="font-semibold text-xl mb-4">Companies</div>

    @if (companyService.error()) {
        <p-message severity="error" [text]="companyService.error()!" styleClass="mb-3" />
    }

    <p-table
        [value]="filteredCompanies()"
        [loading]="companyService.loading()"
        dataKey="Id"
        [rowTrackBy]="trackById"
        [rows]="10"
        [paginator]="true"
        [showGridlines]="true"
        [sortField]="'CreatedAt'"
        [sortOrder]="-1"
        responsiveLayout="scroll"
    >
        <ng-template #caption>
            <div class="flex flex-col gap-3">
                <div class="flex items-center justify-between gap-3 flex-wrap">
                    <div class="flex items-center gap-2 flex-wrap">
                        <p-iconfield>
                            <p-inputicon styleClass="pi pi-search" />
                            <input
                                pInputText
                                type="text"
                                placeholder="Search companies…"
                                [ngModel]="searchTerm()"
                                (ngModelChange)="searchTerm.set($event ?? '')"
                            />
                        </p-iconfield>
                        <p-multiselect
                            [options]="countryFilterOptions()"
                            [ngModel]="selectedCountries()"
                            (ngModelChange)="selectedCountries.set($event ?? [])"
                            placeholder="Country"
                            [filter]="true"
                            [showHeader]="true"
                            [showClear]="true"
                            display="chip"
                            appendTo="body"
                            styleClass="w-full md:w-64"
                        ></p-multiselect>
                        @if (hasActiveFilters()) {
                            <p-button
                                label="Clear"
                                icon="pi pi-filter-slash"
                                severity="secondary"
                                [text]="true"
                                size="small"
                                (onClick)="clearFilters()"
                            />
                        }
                    </div>
                    <div class="flex items-center gap-2">
                        <p-button
                            label="Export CSV"
                            icon="pi pi-download"
                            severity="secondary"
                            outlined
                            [disabled]="filteredCompanies().length === 0"
                            (onClick)="exportCsv()"
                        />
                        <p-button
                            label="Refresh"
                            icon="pi pi-refresh"
                            severity="secondary"
                            outlined
                            [loading]="companyService.loading()"
                            (onClick)="refresh()"
                        />
                        <p-button
                            label="Add company"
                            icon="pi pi-plus"
                            (onClick)="openCreate()"
                        />
                    </div>
                </div>
            </div>
        </ng-template>
        <ng-template #header>
            <tr>
                <th pSortableColumn="CreatedAt" style="width: 11rem">Created at <p-sortIcon field="CreatedAt" /></th>
                <th pSortableColumn="Name">Name <p-sortIcon field="Name" /></th>
                <th pSortableColumn="CountryCode">Country <p-sortIcon field="CountryCode" /></th>
                <th>Address</th>
                <th>Registration details</th>
                <th style="width: 7rem">Websites</th>
                <th style="width: 6rem">Stripe</th>
                <th style="width: 12rem">LiveChat</th>
                <th style="width: 8rem">Actions</th>
            </tr>
        </ng-template>
        <ng-template #body let-company>
            <tr [class.row-highlight]="company.Id === highlightedId()">
                <td>
                    <span class="text-sm text-muted-color">{{ company.CreatedAt | date:'medium' }}</span>
                </td>
                <td>
                    <span class="font-medium">{{ company.Name }}</span>
                </td>
                <td>
                    <div class="flex items-center gap-2">
                        <span [class]="'flag flag-' + company.CountryCode.toLowerCase()" style="width: 18px; height: 12px"></span>
                        <span>{{ countryService.getName(company.CountryCode) }}</span>
                        <span class="text-xs text-muted-color font-mono">({{ company.CountryCode }})</span>
                    </div>
                </td>
                <td>
                    <span class="text-sm text-muted-color whitespace-pre-line break-words">{{ company.Address }}</span>
                </td>
                <td>
                    <span class="text-sm text-muted-color whitespace-pre-line break-words">{{ company.RegistrationDetails }}</span>
                </td>
                <td>
                    @let n = websiteCount(company.Id);
                    @if (n > 0) {
                        <button
                            type="button"
                            class="text-primary hover:underline"
                            (click)="viewWebsites(company)"
                            pTooltip="View websites"
                        >{{ n }}</button>
                    } @else {
                        <span class="text-muted-color">0</span>
                    }
                </td>
                <td>
                    @if (company.Stripe.IsEnabled) {
                        <i class="pi pi-check-circle text-green-500" style="font-size: 0.95rem" pTooltip="Stripe enabled" tooltipPosition="top"></i>
                    } @else {
                        <span class="text-muted-color" pTooltip="Stripe not enabled" tooltipPosition="top">—</span>
                    }
                </td>
                <td>
                    @if (company.LiveChatConfig.GroupId; as id) {
                        <span class="inline-flex items-center gap-1 text-xs font-mono max-w-full" [pTooltip]="id" tooltipPosition="top">
                            <i class="pi pi-check-circle text-green-500" style="font-size: 0.85rem"></i>
                            <span class="truncate">{{ id }}</span>
                        </span>
                    } @else {
                        <span class="text-muted-color" pTooltip="No LiveChat Group ID set" tooltipPosition="top">—</span>
                    }
                </td>
                <td>
                    <div class="flex gap-1">
                        <p-button
                            icon="pi pi-pencil"
                            severity="warn"
                            [rounded]="true"
                            [outlined]="true"
                            pTooltip="Edit"
                            (onClick)="openEdit(company)"
                        />
                        <p-button
                            icon="pi pi-trash"
                            severity="danger"
                            [rounded]="true"
                            [outlined]="true"
                            pTooltip="Delete"
                            (onClick)="openDelete(company)"
                        />
                    </div>
                </td>
            </tr>
        </ng-template>
        <ng-template #emptymessage>
            <tr>
                <td colspan="9" class="text-center py-8 text-muted-color">
                    @if (hasActiveFilters()) {
                        No companies match the current filters.
                    } @else {
                        No companies yet.
                    }
                </td>
            </tr>
        </ng-template>
    </p-table>
</div>

<app-company-edit-dialog
    [visible]="editDialogVisible()"
    (visibleChange)="editDialogVisible.set($event)"
    [company]="editingCompany()"
    (created)="onCompanyCreated($event)"
/>

<app-company-delete-dialog
    [visible]="deleteDialogVisible()"
    (visibleChange)="deleteDialogVisible.set($event)"
    [company]="deleteTarget()"
    [websiteCount]="deleteTarget() ? websiteCount(deleteTarget()!.Id) : 0"
/>
